import type { CollectorSelfProfileOptions } from './collectorSelfProfileOptions';
import { client } from '../client';
import { Address, UserProfile, UserRoleName } from './types/UserProfile';

export interface UpdateMe {
  address?: Address;
  phone_number?: string | null;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  other_insurance_name?: string;
  /** Staff-only "User IDs" field. Max 40 characters, enforced by the backend. */
  external_id?: string;
}

/**
 * @param options.collectorEditingSelf When true with `UserRoleName.Collector`, uses **PATCH /collector/me**.
 *   When false/omitted with Collector, uses **PATCH /collector/users/{user_id}** (managed patient/dependent only;
 *   the API enforces `created_by_id` — never pass the collector’s own user id here).
 */
export const updateMe = async (
  payload: UpdateMe,
  user_id: number,
  userRole?: string,
  options?: CollectorSelfProfileOptions,
): Promise<UserProfile> => {
  const isAdminRole =
    userRole === UserRoleName.Admin ||
    userRole === UserRoleName.SuperAdmin ||
    userRole === UserRoleName.OrganizationAdmin;
  const prefix =
    userRole === UserRoleName.CareAdvisor
      ? '/care_advisor'
      : userRole === UserRoleName.Collector
      ? '/collector'
      : isAdminRole
      ? '/admin'
      : '';
  const useCollectorMe = userRole === UserRoleName.Collector && options?.collectorEditingSelf === true;
  const purifiedPayload: any = {
    address: payload.address,
    email_opt_in: payload.email_opt_in,
    sms_opt_in: payload.sms_opt_in,
    other_insurance_name: payload.other_insurance_name,
  };

  // Distinguish "phone not part of this update" (undefined → leave the stored
  // value untouched) from "phone explicitly cleared" (empty/whitespace → send
  // null so the API deletes it). Sending null rather than "" avoids tripping
  // the server-side phone_number regex.
  if (payload.phone_number !== undefined) {
    purifiedPayload.phone_number = payload.phone_number?.trim() ? payload.phone_number : null;
  }

  // external_id is only accepted on the role-scoped managed-user endpoints
  // (/admin, /care_advisor, /collector `/users/{id}`) — never on `/collector/me`
  // or the unscoped `/users/{id}` a plain self-edit falls back to, so it's only
  // forwarded there even though `payload` may carry it unconditionally.
  if (payload.external_id !== undefined && prefix && !useCollectorMe) {
    purifiedPayload.external_id = payload.external_id;
  }

  const url = useCollectorMe ? '/collector/me' : `${prefix}/users/${user_id}`;
  const { data } = await client.patch<UserProfile>(url, purifiedPayload);

  return data;
};
